import { useCallback, useEffect, useRef } from 'react';
import { adminAPI } from '@/services/api';
import { TabCounts } from '../state/useUserState';

interface UseUserManagementTabsProps {
  // 탭 카운트 상태 (그룹화된 구조)
  setCounts: (partial: Partial<TabCounts>) => void;

  // 새로고침
  triggerRefresh: () => void;

  // 차단목록 로드 함수
  loadBlockedLogs: () => Promise<void>;

  // 컨텍스트 정보
  isMyUserManagement: boolean;
  isAdmin: boolean;
  isAllUserManagement: boolean;
  selectedCustomerId: number | null;

  // 활성 탭 (superadmin만 사용)
  activeTab?: string;
}

/**
 * 사용자 관리 탭 카운트 및 새로고침 로직 hook
 * admin/index.tsx와 superadmin/index.tsx에서 공통으로 사용되는 탭 카운트 로직을 관리합니다.
 */
export const useUserManagementTabs = ({
  setCounts,
  triggerRefresh,
  loadBlockedLogs,
  isMyUserManagement,
  isAdmin,
  isAllUserManagement,
  selectedCustomerId,
  activeTab,
}: UseUserManagementTabsProps) => {
  
  const refreshAllTabsRef = useRef<() => Promise<void>>(async () => {});

  const fetchTabCounts = useCallback(async () => {
    try {
      // "내 이용자 관리" 또는 기업관리자: 자신의 고객사 사용자 수만 직접 조회
      if ((isMyUserManagement || isAdmin) && selectedCustomerId) {
        // 전체 사용자 (ACTIVE + DEACTIVATED)
        const allUsersResponse = await adminAPI.getCustomerUsers(selectedCustomerId, 0, 1000);
        const allUsers = (allUsersResponse.users || []).filter(
          (u: any) => u.status === 'active' || u.status === 'deactivated'
        );

        // 승인대기중 (PENDING + INVITED)
        const pendingResponse = await adminAPI.getPendingUsers(0, 1000);
        const pendingUsers = (pendingResponse.users || []).filter(
          (u: any) => u.customer?.id === selectedCustomerId
        );

        // 차단목록
        let blockedCountValue = 0;
        try {
          const blockedResponse = await adminAPI.getBlockedSignups(selectedCustomerId);
          blockedCountValue = blockedResponse.length || 0;
        } catch {
          blockedCountValue = 0;
        }

        setCounts({
          allUsers: allUsers.length,
          pending: pendingUsers.length,
          blocked: blockedCountValue,
        });
      } else if (isAllUserManagement) {
        // "전체 이용자 관리": 전체 사용자 수 조회
        const countsResponse = await adminAPI.getUserTabCounts();

        // 차단목록: 모든 고객사의 차단목록 조회
        let blockedCountValue = 0;
        try {
          const blockedResponse = await adminAPI.getBlockedSignups(null, 28);
          blockedCountValue = blockedResponse.length || 0;
        } catch {
          blockedCountValue = 0;
        }

        setCounts({
          allUsers: countsResponse.all_users || 0,
          pending: countsResponse.pending || 0,
          blocked: blockedCountValue,
        });
      } else {
        // 기타 경우: 기본값 0
        setCounts({ allUsers: 0, pending: 0, blocked: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch user tab counts:', err);
      setCounts({ allUsers: 0, pending: 0, blocked: 0 });
    }
  }, [isMyUserManagement, isAdmin, isAllUserManagement, selectedCustomerId, setCounts]);
  
  // refreshAllTabs 함수 정의
  useEffect(() => {
    refreshAllTabsRef.current = async () => {
      triggerRefresh();
      // 차단목록도 새로고침
      await loadBlockedLogs();
      // 탭 카운트도 새로고침 (차단목록 업데이트 후)
      await fetchTabCounts();
    };
  }, [triggerRefresh, loadBlockedLogs, fetchTabCounts]);
  
  // refreshAllTabs는 refreshAllTabsRef.current를 호출하는 래퍼 함수
  const refreshAllTabs = useCallback(async () => {
    await refreshAllTabsRef.current();
  }, []);
  
  // 초기 로드 및 refreshKey 변경 시 카운트 갱신
  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);
  
  // superadmin/index.tsx 전용: 차단목록 탭이 아닐 때도 카운트 갱신
  useEffect(() => {
    if (activeTab !== undefined && activeTab !== 'blocked') {
      // 차단목록 탭이 아닐 때는 카운트만 갱신 (데이터는 로드하지 않음)
      const updateBlockedCount = async () => {
        try {
          let blockedCountValue = 0;
          if (isAllUserManagement) {
            const blockedResponse = await adminAPI.getBlockedSignups(null, 28);
            blockedCountValue = blockedResponse.length || 0;
          } else if (selectedCustomerId) {
            const blockedResponse = await adminAPI.getBlockedSignups(selectedCustomerId, 28);
            blockedCountValue = blockedResponse.length || 0;
          }
          setCounts({ blocked: blockedCountValue });
        } catch {
          setCounts({ blocked: 0 });
        }
      };
      updateBlockedCount();
    }
  }, [activeTab, isAllUserManagement, selectedCustomerId, setCounts]);
  
  return {
    fetchTabCounts,
    refreshAllTabs,
    refreshAllTabsRef,
  };
};

